import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Users, 
  Eye,
  Search,
  File,
  FileSpreadsheet,
  FileType,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function DocumentManagement() {
  const [documents, setDocuments] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Upload Dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Assign Dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedVolunteers, setSelectedVolunteers] = useState([]);
  const [sendNotification, setSendNotification] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [volunteerSearch, setVolunteerSearch] = useState('');
  
  // Delete Dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, volsRes] = await Promise.all([
        fetch(`${API}/admin/documents`, { credentials: 'include' }),
        fetch(`${API}/volunteers`, { credentials: 'include' })
      ]);

      if (docsRes.ok) {
        const data = await docsRes.json();
        setDocuments(data);
      }
      if (volsRes.ok) {
        const data = await volsRes.json();
        // Filter for approved volunteers only
        setVolunteers(data.filter(v => v.status === 'Approved'));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 10MB');
      e.target.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, Word, Excel, CSV');
      e.target.value = '';
      return;
    }

    setUploadFile(file);
    if (!uploadTitle) {
      setUploadTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for title
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setUploading(true);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1]; // Remove data:... prefix

        const response = await fetch(`${API}/admin/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: uploadTitle,
            description: uploadDescription,
            filename: uploadFile.name,
            file_type: uploadFile.type,
            file_data: base64Data,
            file_size: uploadFile.size
          })
        });

        if (response.ok) {
          toast.success('Document uploaded successfully');
          setShowUploadDialog(false);
          resetUploadForm();
          fetchData();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to upload document');
        }
        setUploading(false);
      };

      reader.readAsDataURL(uploadFile);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadTitle('');
    setUploadDescription('');
    setUploadFile(null);
  };

  const openAssignDialog = (document) => {
    setSelectedDocument(document);
    setSelectedVolunteers(document.assigned_volunteers || []);
    setSendNotification(true);
    setVolunteerSearch('');
    setShowAssignDialog(true);
  };

  const handleAssign = async () => {
    if (!selectedDocument || selectedVolunteers.length === 0) {
      toast.error('Please select at least one volunteer');
      return;
    }

    setAssigning(true);

    try {
      const response = await fetch(`${API}/admin/documents/${selectedDocument.document_id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          volunteer_ids: selectedVolunteers,
          send_notification: sendNotification
        })
      });

      if (response.ok) {
        toast.success('Document assigned to volunteers');
        setShowAssignDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign document');
      }
    } catch (error) {
      console.error('Assign error:', error);
      toast.error('Failed to assign document');
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;

    try {
      const response = await fetch(`${API}/admin/documents/${documentToDelete.document_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Document deleted');
        setShowDeleteDialog(false);
        setDocumentToDelete(null);
        fetchData();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await fetch(`${API}/admin/documents/${document.document_id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create download link
        const link = window.document.createElement('a');
        link.href = `data:${data.file_type};base64,${data.file_data}`;
        link.download = data.filename;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } else {
        toast.error('Failed to download document');
      }
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || fileType?.includes('csv')) 
      return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (fileType?.includes('word') || fileType?.includes('document')) 
      return <FileType className="w-5 h-5 text-blue-600" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const filteredDocuments = documents.filter(doc =>
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredVolunteers = volunteers.filter(v => {
    const name = `${v.first_name} ${v.last_name}`.toLowerCase();
    return name.includes(volunteerSearch.toLowerCase()) || 
           v.email?.toLowerCase().includes(volunteerSearch.toLowerCase());
  });

  const toggleVolunteer = (volunteerId) => {
    setSelectedVolunteers(prev => 
      prev.includes(volunteerId)
        ? prev.filter(id => id !== volunteerId)
        : [...prev, volunteerId]
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1a3a5c]">Document Management</h1>
            <p className="text-slate-500">Upload and share documents with volunteers</p>
          </div>
          <Button 
            onClick={() => setShowUploadDialog(true)}
            className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a3a5c]">{documents.length}</p>
                  <p className="text-sm text-slate-500">Total Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a3a5c]">
                    {documents.filter(d => d.assigned_volunteers?.length > 0).length}
                  </p>
                  <p className="text-sm text-slate-500">Shared Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Download className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a3a5c]">
                    {formatFileSize(documents.reduce((acc, d) => acc + (d.file_size || 0), 0))}
                  </p>
                  <p className="text-sm text-slate-500">Total Storage</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Documents Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Documents</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredDocuments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No documents found</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowUploadDialog(true)}
                >
                  Upload your first document
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Shared With</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.document_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.file_type)}
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            <p className="text-xs text-slate-500">{doc.filename}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {doc.file_type?.split('/').pop()?.toUpperCase() || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          doc.assigned_volunteers?.length > 0 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {doc.assigned_volunteers?.length || 0} volunteer(s)
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(doc.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAssignDialog(doc)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Assign to Volunteers"
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDocumentToDelete(doc);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>File *</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  {uploadFile ? (
                    <div className="flex items-center justify-center gap-3">
                      {getFileIcon(uploadFile.type)}
                      <div className="text-left">
                        <p className="font-medium">{uploadFile.name}</p>
                        <p className="text-xs text-slate-500">{formatFileSize(uploadFile.size)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadFile(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 mb-2">
                        PDF, Word, Excel, CSV (max 10MB)
                      </p>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                      />
                      <Button
                        variant="outline"
                        onClick={() => window.document.getElementById('file-upload').click()}
                      >
                        Choose File
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Document title"
                />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Brief description of this document..."
                  rows={2}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUploadDialog(false);
                    resetUploadForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile || !uploadTitle}
                  className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assign Document to Volunteers</DialogTitle>
            </DialogHeader>
            {selectedDocument && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg flex items-center gap-3">
                  {getFileIcon(selectedDocument.file_type)}
                  <div>
                    <p className="font-medium">{selectedDocument.title}</p>
                    <p className="text-xs text-slate-500">{selectedDocument.filename}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Search Volunteers</Label>
                  <Input
                    placeholder="Search by name or email..."
                    value={volunteerSearch}
                    onChange={(e) => setVolunteerSearch(e.target.value)}
                  />
                </div>

                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {filteredVolunteers.length === 0 ? (
                    <p className="text-center py-4 text-slate-500">No approved volunteers found</p>
                  ) : (
                    filteredVolunteers.map((volunteer) => {
                      const isSelected = selectedVolunteers.includes(volunteer.volunteer_id);
                      return (
                        <div
                          key={volunteer.volunteer_id}
                          className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                          }`}
                          onClick={() => toggleVolunteer(volunteer.volunteer_id)}
                        >
                          <div 
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected 
                                ? 'bg-[#1a3a5c] border-[#1a3a5c]' 
                                : 'border-slate-300 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">
                              {volunteer.first_name} {volunteer.last_name}
                            </p>
                            <p className="text-xs text-slate-500">{volunteer.email}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <p className="text-sm text-slate-500">
                  {selectedVolunteers.length} volunteer(s) selected
                </p>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="send-notification"
                    checked={sendNotification}
                    onCheckedChange={setSendNotification}
                  />
                  <Label htmlFor="send-notification" className="text-sm cursor-pointer">
                    Send email notification to volunteers
                  </Label>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowAssignDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssign}
                    disabled={assigning || selectedVolunteers.length === 0}
                    className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  >
                    {assigning ? 'Assigning...' : 'Assign Document'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Document</DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              Are you sure you want to delete "{documentToDelete?.title}"? This will also remove it from all assigned volunteers.
            </p>
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
